(() => {
  class AutoVariantGallery {
    constructor(gallery) {
      this.gallery = gallery;

      const mapElement = gallery.querySelector(
        '[data-auto-variant-gallery-map]'
      );

      if (!mapElement) return;

      try {
        this.variantMap = JSON.parse(mapElement.textContent);
      } catch (error) {
        console.error('Variant gallery map could not be parsed.', error);
        return;
      }

      this.section = gallery.closest('.shopify-section');

      if (!this.section) return;

      this.initialize();
    }

    initialize() {
      this.applyCurrentVariant();

      /*
       * We use event delegation because Dawn updates parts
       * of the product form dynamically when a variant changes.
       */
      this.section.addEventListener('change', () => {
        window.setTimeout(() => {
          this.applyCurrentVariant();
        }, 100);
      });

      /*
       * Additional delayed update for Dawn's asynchronous
       * variant handling.
       */
      this.section.addEventListener('change', () => {
        window.setTimeout(() => {
          this.applyCurrentVariant();
        }, 400);
      });
    }

    getVariantInput() {
      return (
        this.section.querySelector(
          'product-form form input[name="id"]'
        ) ||
        this.section.querySelector(
          'form[action*="/cart/add"] input[name="id"]'
        )
      );
    }

    getCurrentVariantId() {
      const input = this.getVariantInput();

      if (!input || !input.value) {
        return null;
      }

      return String(input.value);
    }

    getMainItems() {
      return Array.from(
        this.gallery.querySelectorAll(
          '.product__media-item[data-media-raw-id]'
        )
      );
    }

    getThumbnailItems() {
      return Array.from(
        this.gallery.querySelectorAll(
          '.thumbnail-list__item[data-media-raw-id]'
        )
      );
    }

    getVariantStartPositions() {
      const mainItems = this.getMainItems();

      const positions = [];

      Object.entries(this.variantMap).forEach(
        ([variantId, data]) => {
          if (!data || !data.mediaId) return;

          const mediaId = String(data.mediaId);

          const item = mainItems.find(
            (media) =>
              String(media.dataset.mediaRawId) === mediaId
          );

          if (!item) return;

          positions.push({
            variantId: String(variantId),
            mediaId: mediaId,
            position: Number(item.dataset.mediaPosition)
          });
        }
      );

      positions.sort(
        (a, b) => a.position - b.position
      );

      return positions;
    }

    getGroupForVariant(variantId) {
      const positions = this.getVariantStartPositions();

      const current = positions.find(
        (entry) => entry.variantId === String(variantId)
      );

      if (!current) {
        return null;
      }

      /*
       * Find the next DIFFERENT variant featured image
       * appearing after this one in the product media order.
       */
      const next = positions.find(
        (entry) => entry.position > current.position
      );

      return {
        start: current.position,
        end: next ? next.position - 1 : Infinity,
        mediaId: current.mediaId
      };
    }

    applyCurrentVariant() {
      const variantId = this.getCurrentVariantId();

      if (!variantId) {
        return;
      }

      const group = this.getGroupForVariant(variantId);

      /*
       * If this variant has no featured image,
       * don't interfere with Dawn.
       */
      if (!group) {
        this.showAll();
        return;
      }

      const mainItems = this.getMainItems();
      const thumbnailItems = this.getThumbnailItems();

      let firstVisibleMain = null;
      let firstVisibleThumbnail = null;

      mainItems.forEach((item) => {
        const position = Number(
          item.dataset.mediaPosition
        );

        const visible =
          position >= group.start &&
          position <= group.end;

        this.setVisibility(item, visible);

        if (visible && !firstVisibleMain) {
          firstVisibleMain = item;
        }
      });

      thumbnailItems.forEach((item) => {
        const position = Number(
          item.dataset.mediaPosition
        );

        const visible =
          position >= group.start &&
          position <= group.end;

        this.setVisibility(item, visible);

        if (visible && !firstVisibleThumbnail) {
          firstVisibleThumbnail = item;
        }
      });

      this.activateFirstMedia(
        firstVisibleMain,
        firstVisibleThumbnail
      );

      this.updateCounter();
    }

    setVisibility(element, visible) {
      element.hidden = !visible;
      element.style.display = visible ? '' : 'none';

      element.setAttribute(
        'aria-hidden',
        visible ? 'false' : 'true'
      );
    }

    activateFirstMedia(mainItem, thumbnailItem) {
      if (!mainItem) return;

      /*
       * Remove Dawn's previous active states.
       */
      this.gallery
        .querySelectorAll('.product__media-item.is-active')
        .forEach((item) => {
          item.classList.remove('is-active');
        });

      this.gallery
        .querySelectorAll(
          '.thumbnail[aria-current="true"]'
        )
        .forEach((button) => {
          button.removeAttribute('aria-current');
        });

      mainItem.classList.add('is-active');

      if (thumbnailItem) {
        const button =
          thumbnailItem.querySelector('.thumbnail');

        if (button) {
          button.setAttribute(
            'aria-current',
            'true'
          );
        }
      }

      /*
       * Use Dawn's built-in media-gallery method.
       */
      const mediaId = mainItem.dataset.mediaId;

      if (
        mediaId &&
        typeof this.gallery.setActiveMedia === 'function'
      ) {
        try {
          this.gallery.setActiveMedia(
            mediaId,
            true
          );
        } catch (error) {
          console.warn(
            'Could not activate variant media.',
            error
          );
        }
      }
    }

    updateCounter() {
      const visibleItems =
        this.getMainItems().filter(
          (item) => !item.hidden
        );

      const total =
        this.gallery.querySelector(
          '.slider-counter--total'
        );

      const current =
        this.gallery.querySelector(
          '.slider-counter--current'
        );

      if (total) {
        total.textContent =
          visibleItems.length;
      }

      if (current) {
        current.textContent =
          visibleItems.length > 0 ? '1' : '0';
      }
    }

    showAll() {
      this.getMainItems().forEach(
        (item) => {
          this.setVisibility(item, true);
        }
      );

      this.getThumbnailItems().forEach(
        (item) => {
          this.setVisibility(item, true);
        }
      );

      const total =
        this.gallery.querySelector(
          '.slider-counter--total'
        );

      if (total) {
        total.textContent =
          this.getMainItems().length;
      }
    }
  }


  function initializeAutoVariantGalleries(
    root = document
  ) {
    root
      .querySelectorAll(
        'media-gallery[data-auto-variant-gallery]'
      )
      .forEach((gallery) => {

        if (
          gallery.dataset
            .autoVariantGalleryInitialized ===
          'true'
        ) {
          return;
        }

        gallery.dataset
          .autoVariantGalleryInitialized =
          'true';

        new AutoVariantGallery(gallery);
      });
  }


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeAutoVariantGalleries();
      }
    );

  } else {

    initializeAutoVariantGalleries();

  }


  /*
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAutoVariantGalleries(
        event.target
      );
    }
  );

})();